import { useRef, useState } from "react";

// OpenLayers
import { Draw } from "ol/interaction";
import { unByKey } from "ol/Observable";
import Overlay from "ol/Overlay";

// Context
import { useMap } from "context/Map2dContext";

// Utils
import { unprojectFeature } from "utils/OpenLayers/TransformUtils";

/**
 * Hook options for usePickPoint2D.
 * @typedef {Object} UsePickPoint2DOptions
 * @property {import("ol/style/Style").default} [style] - Style applied to the temporary Draw interaction.
 * @property {function(Object): boolean} [condition] - Predicate receiving the OpenLayers MapBrowserEvent for each pointer event.
 * @property {function({type: "Point", coordinates: number[]}): void} [onEnd] - Called on each completed point pick with a GeoJSON-style Point ({ type: "Point", coordinates }).
 * @property {function(): void} [onCancel] - Called when the interaction is cancelled via stopPickPoint.
 * @property {string} [tooltipClassName] - CSS class used by the floating help tooltip element.
 * @property {function(): string} [getHelpMessage] - Returns the help message shown during pointer move.
 */

/**
 * @typedef {Object} StartPickPoint2DOptions
 * @property {string} [helpMessage] - Optional one-off help message override for this session.
 */

/**
 * Picks a point on the 2D OpenLayers map using a Draw interaction.
 * Keeps the interaction active for multiple clicks and only stops when explicitly cancelled.
 *
 * Encapsulates OL low-level lifecycle (draw interaction, tooltip overlay, pointer listeners,
 * context menu cancellation) so consumers only orchestrate start/stop and react to callbacks.
 *
 * @param {UsePickPoint2DOptions} [options] - Optional configuration for the Draw interaction and callbacks.
 * @returns {{
 *   point: {type: "Point", coordinates: number[]} | null,
 *   isActive: boolean,
 *   startPickPoint: function(StartPickPoint2DOptions=): {started: boolean, reason?: string},
 *   stopPickPoint: function(): void,
 *   clearPoint: function(): void
 * }}
 */
function usePickPoint2D({
  style,
  condition,
  onEnd,
  onCancel,
  tooltipClassName,
  getHelpMessage
} = {}) {
  const mapContext = useMap();
  const map = mapContext?.olmap;

  const [point, setPoint] = useState(null);
  const [isActive, setIsActive] = useState(false);

  const drawRef = useRef(null);
  const drawEndListenerRef = useRef(null);
  const pointerMoveListenerRef = useRef(null);
  const helpTooltipElementRef = useRef(null);
  const helpTooltipRef = useRef(null);
  const helpMessageRef = useRef("");

  function mouseOutHandler2D(evt) {
    if (!evt.dragging) {
      helpTooltipElementRef.current?.classList.add("hidden");
    }
  }

  function pointerMoveHandler2D(evt) {
    if (
      evt.dragging ||
      !helpTooltipElementRef.current ||
      !helpTooltipRef.current
    ) {
      return;
    }
    const nextHelpMessage =
      typeof getHelpMessage === "function"
        ? _resolveHelpMessage()
        : helpMessageRef.current;
    helpTooltipElementRef.current.innerHTML = nextHelpMessage;
    helpTooltipRef.current.setPosition(evt.coordinate);
    helpTooltipElementRef.current.classList.remove("hidden");
  }

  function createHelpTooltip2D() {
    if (!map) {
      return;
    }

    if (helpTooltipElementRef.current?.parentNode) {
      helpTooltipElementRef.current.parentNode.removeChild(
        helpTooltipElementRef.current
      );
    }

    helpTooltipElementRef.current = document.createElement("div");
    helpTooltipElementRef.current.className = tooltipClassName || "";
    helpTooltipRef.current = new Overlay({
      element: helpTooltipElementRef.current,
      offset: [15, 0],
      positioning: "center-left"
    });
    map.addOverlay(helpTooltipRef.current);
  }

  function _removeHelpArtifacts() {
    if (map && helpTooltipRef.current) {
      map.removeOverlay(helpTooltipRef.current);
    }
    helpTooltipRef.current = null;

    if (pointerMoveListenerRef.current) {
      unByKey(pointerMoveListenerRef.current);
      pointerMoveListenerRef.current = null;
    }

    if (map) {
      map.un("mouseout", mouseOutHandler2D);
      map.getViewport().removeEventListener("contextmenu", contextMenuListener);
    }
  }

  function _endInteraction({ cancelled } = { cancelled: false }) {
    if (map && drawRef.current) {
      map.removeInteraction(drawRef.current);
    }
    drawRef.current = null;

    if (drawEndListenerRef.current) {
      unByKey(drawEndListenerRef.current);
      drawEndListenerRef.current = null;
    }

    _removeHelpArtifacts();

    setIsActive(false);

    if (cancelled) {
      setPoint(null);
    }

    if (cancelled && typeof onCancel === "function") {
      onCancel();
    }
  }

  function _onDrawEnd(e) {
    const cloneFeat = e.feature.clone();
    const feature = unprojectFeature(cloneFeat);
    const coordinates = feature.getGeometry().getCoordinates();

    const geojsonPoint = { type: "Point", coordinates };

    setPoint(geojsonPoint);

    if (typeof onEnd === "function") {
      onEnd(geojsonPoint);
    }
  }

  function contextMenuListener(evt) {
    evt.preventDefault();
    _endInteraction({ cancelled: true });
  }

  function _resolveHelpMessage(helpMessage) {
    if (typeof helpMessage === "string") {
      return helpMessage;
    }
    if (typeof getHelpMessage === "function") {
      return getHelpMessage();
    }
    return "";
  }

  function _hasAnyDrawInteraction() {
    if (!map) {
      return false;
    }

    const interactions = map.getInteractions().getArray();
    return interactions.some(interaction => interaction instanceof Draw);
  }

  /**
   * Start a pick session.
   * Returns "missing-map" without side effects when map is unavailable,
   * and "busy" when another Draw interaction is active.
   */
  function startPickPoint({ helpMessage } = {}) {
    if (!map) {
      return { started: false, reason: "missing-map" };
    }

    if (_hasAnyDrawInteraction() || isActive) {
      return { started: false, reason: "busy" };
    }

    helpMessageRef.current = _resolveHelpMessage(helpMessage);

    const drawOptions = {
      type: "Point",
      stopClick: true
    };

    if (style) {
      drawOptions.style = style;
    }

    if (condition) {
      drawOptions.condition = condition;
    }

    const draw = new Draw(drawOptions);

    drawRef.current = draw;
    map.addInteraction(draw);
    drawEndListenerRef.current = draw.on("drawend", _onDrawEnd);

    createHelpTooltip2D();
    pointerMoveListenerRef.current = map.on(
      "pointermove",
      pointerMoveHandler2D
    );
    map.on("mouseout", mouseOutHandler2D);
    map.getViewport().addEventListener("contextmenu", contextMenuListener);

    setIsActive(true);

    return { started: true };
  }

  /**
   * Stop the active pick session.
   * Treated as cancellation: clears current point and calls onCancel callback.
   */
  function stopPickPoint() {
    _endInteraction({ cancelled: true });
  }

  /**
   * Clear only the stored point value.
   * Does not change interaction lifecycle or registered listeners.
   */
  function clearPoint() {
    setPoint(null);
  }

  return { point, isActive, startPickPoint, stopPickPoint, clearPoint };
}

export default usePickPoint2D;
